import type {
  ArcGisCodedValue,
  ArcGisConditionOperator,
} from './arcgisLayerSchema';
import type { FeedPreset } from './automationRuleLogic';

/**
 * Per-field overrides that go beyond ArcGIS type inference. Keep STORMTYPE
 * ordering in sync with `STORM_TYPE_RANK` in
 * `services/coms/src/routes/OrgEventAutomation/evaluateConditions.ts` (dice
 * cannot import from coms).
 */
export type FieldSemantics = {
  /** Overrides type-derived operators when set. */
  operators?: ArcGisConditionOperator[];
  /** Ascending ordinal ladder; used for value select + WHERE expansion. */
  orderedValues?: ArcGisCodedValue[];
  unit?: string;
  helpText?: string;
  /** True when coms `evaluateAttributeCondition` has a dedicated branch. */
  evaluatorAware: boolean;
};

/**
 * Rank for each STORMTYPE code. Mirrors coms `STORM_TYPE_RANK` plus CAT 1–5.
 * TY/TC are rank-6 aliases of HU and are omitted from the dropdown; they are
 * injected into preview WHERE clauses when HU is included.
 */
export const STORMTYPE_RANK: Record<string, number> = {
  DB: 0,
  LO: 0,
  WV: 0,
  EX: 0,
  SD: 1,
  TD: 2,
  SS: 3,
  TS: 4,
  STS: 5,
  HU: 6,
  TY: 6,
  TC: 6,
  'CAT 1': 7,
  'CAT 2': 8,
  'CAT 3': 9,
  'CAT 4': 10,
  'CAT 5': 11,
};

/** Codes that share HU's rank but are not offered in the UI ladder. */
export const STORMTYPE_HU_ALIASES = ['TY', 'TC'] as const;

/**
 * Ascending intensity ladder for the Value dropdown. Codes must match what the
 * evaluator accepts (exact `TS`, not a label).
 */
export const STORMTYPE_ORDERED_VALUES: ArcGisCodedValue[] = [
  { code: 'DB', name: 'Disturbance (DB)' },
  { code: 'LO', name: 'Low (LO)' },
  { code: 'WV', name: 'Wave (WV)' },
  { code: 'EX', name: 'Extratropical (EX)' },
  { code: 'SD', name: 'Subtropical Depression (SD)' },
  { code: 'TD', name: 'Tropical Depression (TD)' },
  { code: 'SS', name: 'Subtropical Storm (SS)' },
  { code: 'TS', name: 'Tropical Storm (TS)' },
  { code: 'STS', name: 'Severe Tropical Storm (STS)' },
  { code: 'HU', name: 'Hurricane (HU)' },
  { code: 'CAT 1', name: 'Category 1 (CAT 1)' },
  { code: 'CAT 2', name: 'Category 2 (CAT 2)' },
  { code: 'CAT 3', name: 'Category 3 (CAT 3)' },
  { code: 'CAT 4', name: 'Category 4 (CAT 4)' },
  { code: 'CAT 5', name: 'Category 5 (CAT 5)' },
];

const FULL_COMPARE_OPS: ArcGisConditionOperator[] = [
  '>=',
  '>',
  '<=',
  '<',
  '==',
];

export const TROPICAL_FIELD_SEMANTICS: Record<string, FieldSemantics> = {
  STORMTYPE: {
    operators: FULL_COMPARE_OPS,
    orderedValues: STORMTYPE_ORDERED_VALUES,
    helpText:
      'Compared by storm intensity rank (not alphabetical). Tropical Storm (TS) and stronger includes hurricanes.',
    evaluatorAware: true,
  },
  MAXWIND: {
    unit: 'kt',
    helpText: 'Maximum sustained wind in knots.',
    evaluatorAware: true,
  },
  MAX_WIND: {
    unit: 'kt',
    helpText: 'Maximum sustained wind in knots.',
    evaluatorAware: true,
  },
  MAX_SS: {
    unit: 'category',
    helpText: 'Maximum Saffir–Simpson category (0–5).',
    evaluatorAware: true,
  },
  SSNUM: {
    unit: 'category',
    helpText: 'Saffir–Simpson category number (0–5).',
    evaluatorAware: true,
  },
};

export function semanticsForPreset(
  preset: FeedPreset | string | undefined
): Record<string, FieldSemantics> {
  if (preset === 'tropical') return TROPICAL_FIELD_SEMANTICS;
  return {};
}

export function resolveFieldSemantics(
  preset: FeedPreset | string | undefined,
  fieldName: string | null | undefined
): FieldSemantics | undefined {
  if (!fieldName?.trim()) return undefined;
  const table = semanticsForPreset(preset);
  const key = fieldName.trim().toUpperCase();
  if (table[key]) return table[key];
  // Case-insensitive fallback for mixed-case schema names.
  const match = Object.entries(table).find(
    ([name]) => name.toUpperCase() === key
  );
  return match?.[1];
}

export function stormTypeRank(value: string): number {
  const key = value.trim().toUpperCase();
  if (key in STORMTYPE_RANK) return STORMTYPE_RANK[key];
  const cat = /^CAT\s*(\d)$/i.exec(key);
  if (cat) return 6 + Number(cat[1]);
  return -1;
}

/**
 * Codes that satisfy an ordinal comparison against `threshold`, including TY/TC
 * aliases whenever HU is included.
 */
export function ordinalCodesMatching(
  orderedValues: ArcGisCodedValue[],
  operator: ArcGisConditionOperator,
  threshold: string
): string[] {
  const right = stormTypeRank(threshold);
  if (right < 0) return [];

  const matching: string[] = [];
  for (const entry of orderedValues) {
    const code = String(entry.code);
    const left = stormTypeRank(code);
    if (left < 0) continue;
    let ok = false;
    switch (operator) {
      case '>':
        ok = left > right;
        break;
      case '>=':
        ok = left >= right;
        break;
      case '<':
        ok = left < right;
        break;
      case '<=':
        ok = left <= right;
        break;
      case '==':
        ok = left === right;
        break;
    }
    if (ok) matching.push(code);
  }

  // Inject TY/TC whenever HU is in the result set (same rank as HU).
  if (matching.includes('HU')) {
    for (const alias of STORMTYPE_HU_ALIASES) {
      if (!matching.includes(alias)) matching.push(alias);
    }
  }

  return matching;
}
